import Vue from 'vue'
import axios from 'axios'
import TokenService from '../../common/token.service'
import config from './config/translator.config.js'

/**
 * Store centralisé pour le module de traduction IA.
 * Gère l'état réactif des erreurs, les appels API vers le middleware
 * et la gestion du token d'authentification.
 *
 * Exposé en tant que singleton via `export default new StoreTranslator()`.
 */
class StoreTranslator {

    constructor() {
        // État réactif Vue : erreur globale à afficher dans l'UI
        this.state = Vue.observable({
            errorMessage: '',
            snackbars: []
        })

        // Compatibilité avec les composants qui lisent encore store.snackbars
        this.snackbars = this.state.snackbars
    }

    /**
     * Réinitialise le message d'erreur global.
     */
    clearErrorMessage() {
        this.state.errorMessage = ''
    }

    /**
     * Enregistre un message d'erreur global pour affichage dans l'UI.
     * @param {string} message - Message d'erreur à afficher.
     */
    setErrorMessage(message) {
        this.state.errorMessage = message
        this.pushSnackbar({ message, color: 'error', icon: 'mdi-alert-circle-outline' })
    }

    /**
     * Ajoute une notification dans la pile de snackbars.
     * @param {{ message: string, color: string, icon: string }} snackbar
     */
    pushSnackbar(snackbar) {
        this.state.snackbars.push(snackbar)
    }

    /**
     * Affiche un message informatif via snackbar.
     * @param {string} message
     */
    setInfoMessage(message) {
        this.pushSnackbar({ message, color: 'info', icon: 'mdi-information-outline' })
    }

    /**
     * Affiche un message de succès via snackbar.
     * @param {string} message
     */
    setSuccessMessage(message) {
        this.pushSnackbar({ message, color: 'success', icon: 'mdi-check-circle-outline' })
    }

    /**
     * Rafraîchit le token utilisateur s'il est expiré.
     * Utilise le contexte Vue global `$ansPublicTranslatorAi` pour accéder aux tokens.
     */
    async refreshUserTokenIfExpired() {
        const ctx = Vue.prototype.$ansPublicTranslatorAi
        if (!ctx) return
        await TokenService.refreshUserTokenIfExpired(ctx, axios)
    }

    /**
     * Soumet un feedback (vote positif ou négatif) pour une traduction.
     * Appelle POST /api/translate/feedback avec les informations de la traduction.
     *
     * @param {Object} params
     * @param {boolean} params.positive       - true = vote positif, false = vote négatif
     * @param {string}  params.inputText      - Texte source soumis à la traduction
     * @param {string}  params.translatedText - Traduction produite par le modèle
     * @param {number}  params.ordre          - Ordre du résultat (1 par défaut)
     * @param {string}  [params.terme]        - Correction proposée par l'utilisateur
     * @param {string}  [params.commentaire]  - Commentaire libre de l'utilisateur
     * @param {string}  [params.modelId]      - Identifiant du modèle utilisé
     * @param {string}  [params.direction]    - Direction de traduction (ex: "fr-en")
     */
    async soumettreVote({ positive, inputText, translatedText, ordre, terme, commentaire, modelId, direction }) {
        await this.refreshUserTokenIfExpired()
        this.clearErrorMessage()
        try {
            await axios.post(
                Vue.prototype.$ansPublicTranslatorAi.middlewareBaseUrl + '/api/translate/feedback',
                {
                    sourceText: inputText,
                    originalTranslation: translatedText,
                    positive,
                    userTranslation: terme || null,
                    comment: commentaire || null,
                    modelId: modelId || null,
                    direction: direction || null,
                    order: ordre ?? 1
                },
                { headers: this.buildAuthorizationHeader() }
            )
            this.setSuccessMessage('Vote envoyé. Merci pour votre contribution !')
        } catch (err) {
            const message = err?.response?.data?.message || config.store.messages.errorFeedback
            this.setErrorMessage(message)
        }
    }

    /**
     * Soumet un fichier à traduire via POST /api/translate/file (multipart/form-data).
     * Le middleware retourne un fichier .xlsx qui est téléchargé automatiquement.
     * En cas d'erreur, affiche une snackbar et propage l'exception.
     *
     * @param {Object} params
     * @param {File}   params.file    - Fichier à traduire
     * @param {string} params.modelId - Identifiant du modèle à utiliser
     * @param {string} [params.direction] - Direction de traduction (ex: "fr-en")
     * @returns {Promise<{fileName: string}>} Métadonnées du fichier téléchargé
     */
    /**
     * @param {Object} params
     * @param {File}   params.file
     * @param {string} params.modelId
     * @param {string} [params.direction]
     * @param {Object} [params.outputColumns] - { scoreConfiance, synonymes, termesProches, sources, themes }
     */
    async soumettreFichierATraduire({ file, modelId, direction, outputColumns = {} }) {
        await this.refreshUserTokenIfExpired()
        this.clearErrorMessage()
        const formData = new FormData()
        formData.append('modelId', modelId)
        formData.append('direction', direction || '')
        formData.append('file', file)
        // Map outputColumns to API params (TranslateFileRequest fields)
        formData.append('withConfidenceScore', outputColumns.scoreConfiance ? 'true' : 'false')
        formData.append('withSynonyms', outputColumns.synonymes ? 'true' : 'false')
        formData.append('withNearTerms', outputColumns.termesProches ? 'true' : 'false')
        formData.append('withSources', outputColumns.sources ? 'true' : 'false')
        formData.append('withThemes', outputColumns.themes ? 'true' : 'false')
        try {
            const response = await axios.post(
                Vue.prototype.$ansPublicTranslatorAi.middlewareBaseUrl + '/api/translate/file',
                formData,
                {
                    responseType: 'blob',
                    headers: { 'Content-Type': 'multipart/form-data', ...this.buildAuthorizationHeader() }
                }
            )
            const fileName = this.extractDownloadFileName(response?.headers)
            this.downloadBlobFile(response?.data, fileName)
            this.setSuccessMessage('Fichier traduit téléchargé avec succès.')
            return { fileName }
        } catch (err) {
            const message = err?.response?.data?.message || config.store.messages.errorTranslationFile
            this.setErrorMessage(message)
            throw err
        }
    }

    /**
     * Extrait le nom de fichier depuis l'en-tête Content-Disposition.
     * @param {Object} headers - En-têtes HTTP de la réponse axios.
     * @returns {string}
     */
    extractDownloadFileName(headers) {
        const contentDisposition = headers?.['content-disposition'] || headers?.['Content-Disposition'] || ''
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
        if (utf8Match?.[1]) {
            return decodeURIComponent(utf8Match[1])
        }

        const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
        if (asciiMatch?.[1]) {
            return asciiMatch[1]
        }

        return 'traduction.xlsx'
    }

    /**
     * Déclenche le téléchargement navigateur d'un Blob.
     * @param {Blob} blob - Fichier binaire à télécharger.
     * @param {string} fileName - Nom du fichier téléchargé.
     */
    downloadBlobFile(blob, fileName) {
        if (!blob) return
        const objectUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(objectUrl)
    }

    /**
     * Soumet un texte libre à traduire.
     *
     * NOTE : cette méthode retourne actuellement une réponse MOCK en attendant
     * l'intégration réelle avec le middleware. Le vrai appel API est commenté ci-dessous.
     *
     * Le paramètre `model` est encodé sous la forme "modelId|direction".
     *
     * @param {Object} params
     * @param {string} params.text  - Texte à traduire
     * @param {string} params.model - Modèle encodé "modelId|direction"
        * @returns {Promise<{ translation: string, confidenceScore: number, topTenTranslations?: Array<{translation: string, score: number}> }>}
     */
    async soumettreTermeATraduire({ text, model }) {
        const [modelId, direction] = (model || '').split('|')
        await this.refreshUserTokenIfExpired()
        this.clearErrorMessage()
        try {
            const response = await axios.post(
                Vue.prototype.$ansPublicTranslatorAi.middlewareBaseUrl + '/api/translate',
                {
                    modelId: modelId,
                    direction: direction,
                    text: text
                },
                { headers: this.buildAuthorizationHeader() }
            )

            return response.data
        } catch (err) {

            // Affiche le message d'erreur spécifique du middleware si disponible (ex: dépassement de quota) pour les erreurs 400
            if (err?.response?.status === 400 && err?.response?.data) {
                const msg = typeof err.response.data === 'string' ? err.response.data : (err.response.data.message || '')
                if (msg) {
                    this.setErrorMessage(msg)
                }
            } else {
                this.setErrorMessage(config.store.messages.errorTranslation)
            }
            throw err
        }
    }


    /**
     * Récupère les modèles de traduction activés depuis le middleware.
     * Appelle GET /api/translate/configs et retourne les modèles activés
     * au format { value, label } utilisable dans un <select>.
     *
     * La clé `value` encode modelId et direction sous la forme "modelId|direction".
     * Le résultat est mis en cache dans `this.state.modelOptions` pour éviter les appels répétés.
     *
     * @returns {Promise<Array<{value: string, label: string}>>}
     */
    async recupererModelAI() {
        // Retourne le cache si déjà chargé
        if (this.state.modelOptions) {
            return this.state.modelOptions
        }

        this.clearErrorMessage()
        try {
            const response = await axios.get(
                Vue.prototype.$ansPublicTranslatorAi.middlewareBaseUrl + '/api/translate/configs',

            )
            const models = response.data.models || []
            // Ne conserve que les modèles activés et les reformate pour un <select>
            this.state.modelOptions = models
                .filter(m => m.activated)
                .map(m => ({
                    value: m.modelId + '|' + (m.direction || ''),
                    label: m.displayLabel
                }))
            return this.state.modelOptions
        } catch (err) {
            const message = err?.response?.data?.message || config.store.messages.errorLoadModels
            this.setErrorMessage(message)
            return []
        }
    }

    /**
     * Construit l'en-tête HTTP d'autorisation Bearer à partir du token d'accès courant.
     * Retourne un objet vide si aucun token n'est disponible.
     * @returns {{ Authorization: string } | {}}
     */
    buildAuthorizationHeader() {
        const token = Vue.prototype.$ansPublicTranslatorAi?.tokens?.accessToken
        return token ? { 'Authorization': 'Bearer ' + token } : {}
    }

}

export default new StoreTranslator()
